import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, Textarea, useDisclosure } from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { ScenarioResultProto, ScenariosProcessingOutput } from "build-ts/schedule";
import { NodeInsights } from "containers/Interfaces";
import { AnalysisTreeNodeProto, AnalysisTreeProto } from "build-ts/analysis";
import Artifacts from "components/agentChat/Artifacts";
import SplitView from "components/common/SplitView";
import LeftSideBarChatPage from "components/agentChat/LeftSideBarChatPage";
import Chat, { ChatMessage } from "components/agentChat/Chat";
import { AnalysisTreeSessionKey } from "build-ts/stateful_api";
import { fetchChatSession, initializeChatSession, saveSession, fetchListOfChatSessions, deleteChatSession } from "services/AgentChatService";
import { AgentChatContextProvider, useAgentChat } from "context/AgentChatContext";
import { DarwinAgentType } from "build-ts/common";
import { AgentInputProto, ChatClientType, ChatRequest } from "build-ts/chat_session";
import { ChatMessageType, chatMessageTypeFromJSON, DarwinHeader } from "build-ts/common";
import { CustomAgentProto } from "build-ts/custom_agent";

interface ChatPageContentProps {
	hideArtifacts?: boolean;
	systemPrompt?: string;
	showSystemPromptButton?: boolean;
	sessionId?: string;
	isFromEvaPlayground?: boolean;
}

const ChatPageContent: React.FC<ChatPageContentProps> = ({
	hideArtifacts = false,
	systemPrompt = "",
	showSystemPromptButton = false,
	sessionId,
	isFromEvaPlayground = false
}) => {
  	const navigate = useNavigate();
	const { id: chatSessionId } = useParams();

  	const [agentId, setAgentId] = useState<string | undefined>("");
  	const [runId, setRunId] = useState<string | undefined>("");

	const [treeHeaders, setTreeHeaders] = useState<Header[]>([]);
	const [scenarioResults, setScenarioResults] = useState<ScenarioResultProto[]>([]);
	const [synthesisResult, setSynthesisResult] = useState<ScenariosProcessingOutput>();
	const [contextualisationResult, setContextualisationResult] = useState<ScenariosProcessingOutput>();
	const [isLoadingTableData, setIsLoadingTableData] = useState(false);
	const [selectedNodesToShowTables, setSelectedNodesToShowTables] = useState<{node: NodeInsights, loading: boolean}[]>([]);
	const [isArtifactsPanelVisible, setIsArtifactsPanelVisible] = useState(false);
	const [selectedFindingsIds, setSelectedFindingsIds] = useState<number[]>([]);
	const [showSynthesis, setShowSynthesis] = useState(false);
	const [showContextualization, setShowContextualization] = useState(false);
	// Track the order of artifacts based on when they were clicked (most recent first)
	const [artifactOrder, setArtifactOrder] = useState<string[]>([]);
	// Loading state for chat
	const [isLoadingChat, setIsLoadingChat] = useState(false);
	const [treeSessions, setTreeSessions] = useState<AnalysisTreeSessionKey[]>([]);
	const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
	// Sidebar state
	const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
	// System prompt modal state
	const { isOpen: isSystemPromptModalOpen, onOpen: onSystemPromptModalOpen, onClose: onSystemPromptModalClose } = useDisclosure();

	const { trees, nodeDataMap, setNodeDataMap, getTrees, setChatId, listOfNodes, getNodeKgObservationData, chatId } = useAgentChat();
  const [isSaving, setIsSaving] = useState(false);
  const [chatSessionsList, setChatSessionsList] = useState<DarwinHeader[]>([]);
  const [agentDetails, setAgentDetails] = useState<CustomAgentProto>();

	useEffect(() => {
		if(treeHeaders.length > 0){
			treeHeaders.forEach(header => {
				getTrees(header.id!);
			});
		}
	}, [treeHeaders]);

	// Fetch list of chat sessions when page loads
  const fetchChatSessionsList = async () => {
    const chatSessionsResponse = await fetchListOfChatSessions();
    if(chatSessionsResponse && chatSessionsResponse.headers){
      setChatSessionsList(chatSessionsResponse.headers);
    }
  };

	useEffect(() => {
    fetchChatSessionsList();
  }, []);

// Auto-open system prompt modal when coming from EvaPlayground
useEffect(() => {
    if (isFromEvaPlayground && systemPrompt) {
        onSystemPromptModalOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isFromEvaPlayground, systemPrompt]);

  // Function to fetch the chat history - new darwin
  const fetchChatHistory = async (sessionId: string) => {
    const chatResponse = await fetchChatSession(sessionId, true);
    if(chatResponse?.chat_id){
      setChatId(chatResponse.chat_id);
    }
    const convertedMessages: ChatMessage[] = [];
    for(const msg of chatResponse.messages){
      if(chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.HUMAN && msg.content){
        convertedMessages.push({
          author: "user",
          content: msg.content
        });
      }
      else if(chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.REASONING && msg.content){
        convertedMessages.push({
          author: "reasoning",
          content: "",
          artifacts: msg.artifacts
        });
      }
      else if(chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.RESPONSE && msg.content){
        convertedMessages.push({
          author: "ai",
          content: msg.content
        });
      }
    }
    setChatMessages(convertedMessages);
  };

  // Function to initialize the chat session
  const handleChatSessionInitialization = async () => {
    const chatRequest = ChatRequest.create({
      agent_type: DarwinAgentType.ADD_NUMBERS_TOOL_AGENT,
      client_type: ChatClientType.FRIDA,
      agent_input: AgentInputProto.create({
        trees: agentDetails?.agent_runs[0].agent_run?.tree_headers.map((tree) => AnalysisTreeProto.create({
          header: tree
        })),
      })
    });
    const chatResponse = await initializeChatSession(chatRequest);
    if(chatResponse?.chat_id){
      setChatId(chatResponse.chat_id);
      // Update URL with new chat session ID
      navigate(`/chat/${chatResponse.chat_id}`, { replace: true });
    }
    setIsLoadingChat(false);
    return;
  };

	//Effect to fetch the job run details or initialize EvaPlayground session
	useEffect(()=>{
		const fetchChat = async () => {
			// Handle EvaPlayground case
			if (isFromEvaPlayground && sessionId) {
				setChatId(sessionId);
				setIsLoadingChat(false);
				return;
			}

			// If chatSessionId exists in URL, load the existing chat session
			// Only fetch if chatId doesn't match (meaning we didn't just initialize it)
			// This prevents unnecessary fetch when starting a new chat from sidebar
			if(chatSessionId && chatId !== chatSessionId){
				try {
					setIsLoadingChat(true);
					await fetchChatHistory(chatSessionId);
				} catch (error) {
					console.error("Error loading chat session:", error);
				} finally {
					setIsLoadingChat(false);
				}
			} else if(chatSessionId && chatId === chatSessionId){
				// Chat was just initialized, no need to fetch
				setIsLoadingChat(false);
			}
		};
		fetchChat();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFromEvaPlayground, sessionId, chatSessionId, chatId]);

	// Function to handle closing the artifacts panel
	const handleCloseArtifactsPanel = () => {
		setIsArtifactsPanelVisible(false);
	};

	// Function to handle table click from ReasoningTree
	const handleTableClick = async (nodeId: string) => {
		if (treeHeaders.length > 0) {
			// Check if data is already loaded for this nodeId
			const isNodeAlreadyInTables = selectedNodesToShowTables.some(
				item => item.node.current_node.id === nodeId
			);

			if (isNodeAlreadyInTables) {
				// Show artifacts panel if not already visible and update order
				setIsArtifactsPanelVisible(true);
				updateArtifactOrder(`table-${nodeId}`);
				return;
			}
			// Show artifacts panel and set loading
			setIsArtifactsPanelVisible(true);
			setIsLoadingTableData(true);

			try {
				const node = nodeDataMap.get(nodeId); // fetch AnalysisTreeNodeProto from NodeDataMap
				if (node) {
					const response = node;
					let nodeInsights: NodeInsights;
					if(response){
						nodeInsights = {
							current_node: response as AnalysisTreeNodeProto,
							insights: undefined
						};
					}

					// Add to selectedNodesToShowTables array (FIFO with limit of 12)
					setSelectedNodesToShowTables((prevNodes) => {
						const newNode = {
							node: nodeInsights,
							loading: false
						};

						if (prevNodes.length >= 12) {
							return [newNode, ...prevNodes.slice(0, -1)];
						}

						return [newNode, ...prevNodes];
					});

					// Update artifact order
					updateArtifactOrder(`table-${nodeId}`);
				}
				else {
					const node = listOfNodes.get(nodeId);
					if(node) {
						const response = await getNodeKgObservationData(node);
						let nodeInsights: NodeInsights;
						if(response){
							nodeInsights = {
								current_node: response as AnalysisTreeNodeProto,
								insights: undefined
							};
						}

						// Add to selectedNodesToShowTables array (FIFO with limit of 12)
						setSelectedNodesToShowTables((prevNodes) => {
							const newNode = {
								node: nodeInsights,
								loading: false
							};

							if (prevNodes.length >= 12) {
								return [newNode, ...prevNodes.slice(0, -1)];
							}

							return [newNode, ...prevNodes];
						});

						// Update artifact order
						updateArtifactOrder(`table-${nodeId}`);
					}
				}
			} catch (error) {
				console.error("Error fetching table data:", error);
			} finally {
				setIsLoadingTableData(false);
			}
		}
	};

	// Helper function to update artifact order (most recent first)
	const updateArtifactOrder = (artifactId: string) => {
		setArtifactOrder((prevOrder) => {
			// Remove the artifact if it already exists, then add it to the front
			const filteredOrder = prevOrder.filter(id => id !== artifactId);
			return [artifactId, ...filteredOrder];
		});
	};

	// Function to clear all tables and findings and synthesis and contextualization
	const handleClearAllTables = () => {
		setIsLoadingTableData(false);
		setSelectedNodesToShowTables([]);
		setSelectedFindingsIds([]);
		setShowSynthesis(false);
		setShowContextualization(false);
		setArtifactOrder([]);
	};

	// Function to remove a specific table
	const handleRemoveTable = (nodeId: string) => {
		setSelectedNodesToShowTables((prev) =>
			prev.filter((n) => n.node.current_node.id !== nodeId)
		);
		// Remove from artifact order
		setArtifactOrder((prev) => prev.filter(id => id !== `table-${nodeId}`));
	};

	// Function to handle findings click
	const handleFindingsClick = (scenarioId: number) => {
		// Find the scenario result by scenario_id
		const scenarioResult = scenarioResults.find(result => result.scenario_id === scenarioId);

		if (scenarioResult && scenarioResult.findings) {
			// Check if findings already exists
			const existingFindings = selectedFindingsIds.includes(scenarioId);

			if (existingFindings) {
				// Show artifacts panel if not already visible and update order
				setIsArtifactsPanelVisible(true);
				updateArtifactOrder(`findings-${scenarioId}`);
				return;
			}

			// Add to findings IDs array (FIFO with limit of 12)
			setSelectedFindingsIds((prevIds) => {
				if (prevIds.length >= 12) {
					return [scenarioId, ...prevIds.slice(0, -1)];
				}
				return [scenarioId, ...prevIds];
			});

			// Update artifact order and show artifacts panel
			updateArtifactOrder(`findings-${scenarioId}`);
			setIsArtifactsPanelVisible(true);
		}
	};

	// Function to remove specific findings
	const handleRemoveFindings = (scenarioId: number) => {
		setSelectedFindingsIds((prev) =>
			prev.filter((id) => id !== scenarioId)
		);
		// Remove from artifact order
		setArtifactOrder((prev) => prev.filter(id => id !== `findings-${scenarioId}`));
	};

	// Function to handle synthesis click
	const handleSynthesisClick = () => {
		if (synthesisResult && synthesisResult.output_string) {
			setShowSynthesis(true);
			updateArtifactOrder("synthesis");
			setIsArtifactsPanelVisible(true);
		}
	};

	// Function to handle contextualization click
	const handleContextualizationClick = () => {
		if (contextualisationResult && contextualisationResult.output_string) {
			setShowContextualization(true);
			updateArtifactOrder("contextualization");
			setIsArtifactsPanelVisible(true);
		}
	};

	// Function to remove synthesis
	const handleRemoveSynthesis = () => {
		setShowSynthesis(false);
		setArtifactOrder((prev) => prev.filter(id => id !== "synthesis"));
	};

	// Function to remove contextualization
	const handleRemoveContextualization = () => {
		setShowContextualization(false);
		setArtifactOrder((prev) => prev.filter(id => id !== "contextualization"));
	};

	// Sidebar handlers
	const handleToggleSidebar = () => {
		setIsSidebarExpanded(!isSidebarExpanded);
	};

	// Handle chat session click
	const handleChatSessionClick = async (sessionId: string) => {
		try {
			await fetchChatHistory(sessionId);
			// Update URL
			navigate(`/chat/${sessionId}`, { replace: true });
		} catch (error) {
			console.error("Error fetching chat history:", error);
		}
	};

	// Handle chat session delete
	const handleChatSessionDelete = async (sessionId: string) => {
		try {
			// Show confirmation dialog
			const confirmed = window.confirm("Are you sure you want to delete this chat session?");
			if (!confirmed) return;

			await deleteChatSession(sessionId);

			// If the deleted session is the current one, clear messages and navigate to base chat page
			if (chatId === sessionId) {
				setChatMessages([]);
				setChatId("");
				navigate("/chat", { replace: true });
			}

			// Refresh the chat sessions list
			await fetchChatSessionsList();
		} catch (error) {
			console.error("Error deleting chat session:", error);
		}
	};

  return (
		<Box height="100%" width="100%" overflow="hidden" position="relative">
			{/* Left Sidebar */}
			<LeftSideBarChatPage
				isExpanded={isSidebarExpanded}
				onToggle={handleToggleSidebar}
				//onNewChat={handleNewChat}
				onViewSystemPrompt={isFromEvaPlayground ? onSystemPromptModalOpen : undefined}
				onSaveChat={isFromEvaPlayground ? async () => {
					if (!chatId || isSaving) return;
					setIsSaving(true);
					try {
						await saveSession(chatId);
					} finally {
						setIsSaving(false);
					}
				} : undefined}
				showSystemPromptButton={isFromEvaPlayground && showSystemPromptButton && !!systemPrompt}
				isSaving={isSaving}
				canSave={!!chatId && !isSaving}
        		setAgentId={setAgentId}
        		setRunId={setRunId}
        		setAgentDetails={setAgentDetails}
        		setChatMessages={setChatMessages}
				chatSessionsList={chatSessionsList}
				currentChatSessionId={chatId || chatSessionId}
				onChatSessionClick={handleChatSessionClick}
				onChatSessionDelete={handleChatSessionDelete}
				isFromEvaPlayground={isFromEvaPlayground}
			/>

			{/* Main Content Area */}
			<Box
				marginLeft={isSidebarExpanded ? "240px" : "48px"}
				height="100%"
				width={isSidebarExpanded ? "calc(100vw - 240px)" : "calc(100vw - 48px)"}
				transition="margin-left 0.3s ease-in-out, width 0.3s ease-in-out"
				overflow="hidden"
			>

				<Box height="100%">
					<SplitView
						leftPanel={
							<Box
								height="100%"
								width="100%"
								bg="bg.surface"
								boxShadow="sm"
								borderRadius="lg"
								p={4}
							>
								<Chat
									metadataId={agentId}
									userId={runId}
                  					chatSessionId={chatId || chatSessionId}
									treeSessions={treeSessions}
									trees={trees}
									chatMessages={chatMessages}
									setChatMessages={setChatMessages}
									isLoadingChat={isLoadingChat}
									nodeDataMap={nodeDataMap}
									setNodeDataMap={setNodeDataMap}
									// Pass initial reasoning tree data
									treeHeaders={treeHeaders}
									scenarioResults={scenarioResults}
									synthesisResult={synthesisResult}
									contextualisationResult={contextualisationResult}
									onTableClick={handleTableClick}
									onFindingsClick={handleFindingsClick}
									onSynthesisClick={handleSynthesisClick}
									onContextualizationClick={handleContextualizationClick}
									isSessionMode={isFromEvaPlayground}
									sessionId={sessionId}
									chatSessionsList={chatSessionsList}
									fetchChatSessionsList={fetchChatSessionsList}
									isFromEvaPlayground={isFromEvaPlayground}
								/>
							</Box>
						}
						rightPanel={
							!hideArtifacts ? (
								<Artifacts
									selectedNodesToShowTables={selectedNodesToShowTables}
									isLoadingTableData={isLoadingTableData}
									onClearAllTables={handleClearAllTables}
									onClosePanel={handleCloseArtifactsPanel}
									onRemoveTable={handleRemoveTable}
									selectedFindingsIds={selectedFindingsIds}
									onRemoveFindings={handleRemoveFindings}
									scenarioResults={scenarioResults}
									showSynthesis={showSynthesis}
									showContextualization={showContextualization}
									synthesisResult={synthesisResult}
									contextualisationResult={contextualisationResult}
									onRemoveSynthesis={handleRemoveSynthesis}
									onRemoveContextualization={handleRemoveContextualization}
									artifactOrder={artifactOrder}
								/>
							) : null
						}
						isRightPanelVisible={!hideArtifacts && isArtifactsPanelVisible}
						defaultRightWidth={45}
					/>
				</Box>
			</Box>

			{/* System Prompt Modal */}
			<Modal isOpen={isSystemPromptModalOpen} onClose={onSystemPromptModalClose} size="xl">
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>System Prompt</ModalHeader>
					<ModalCloseButton />
					<ModalBody pb={6}>
						<Textarea
							value={systemPrompt}
							readOnly
							rows={25}
							resize="vertical"
							bg="gray.50"
							border="1px solid"
							borderColor="gray.200"
						/>
					</ModalBody>
				</ModalContent>
			</Modal>
		</Box>
  );
};

interface ChatPageProps {
	hideArtifacts?: boolean;
	systemPrompt?: string;
	showSystemPromptButton?: boolean;
	sessionId?: string;
	isFromEvaPlayground?: boolean;
}

const ChatPage: React.FC<ChatPageProps> = (props) => {

  return (
    <AgentChatContextProvider>
      <ChatPageContent {...props} />
    </AgentChatContextProvider>
  );
};

export default ChatPage;